/**
 * ABMHCSiteManager — admin editor for the org_site_cms ABMHC tenant.
 *
 * Lets super-admins edit the public ABMHC website (`/abmhc`) without touching
 * code:
 *   • Site Info — name, theme colors, SEO metadata, publish toggle
 *   • Blocks — enable/disable + edit each block's props (structured form per
 *     known block type, JSON fallback for unknown types)
 *   • Domains — manage subdomain/custom-domain aliases that route to this site
 *
 * Wire: GET /api/sites/admin/abmhc + PUT /api/sites/admin/abmhc
 */
import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Save, Eye, Plus, Trash2, Loader2, ExternalLink, Globe } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import MediaField from './MediaField';

const SITE_SLUG_FALLBACK = 'abmhc';

const BLOCK_LABELS = {
  hero: 'Hero',
  about: 'About',
  services: 'Services',
  doctors: 'Doctors / Team',
  patient_info: 'Patient Info',
  news: 'News & Announcements',
  gallery: 'Gallery',
  donate: 'Donate',
  contact: 'Contact',
  footer: 'Footer',
  // Church-specific
  leadership: 'Leadership',
  service_times: 'Service Times',
  ministries: 'Ministries',
  sermons: 'Sermons & Media',
  tribute: 'Tribute Wall',
  events: 'Events',
  livestream: 'Livestream',
  prayer_requests: 'Prayer Requests',
  // Hospital-specific
  milestones: 'Project Milestones',
  quick_links: 'Quick Links',
  funding_progress: 'Funding Progress',
  newsletter: 'Newsletter Signup',
  patient_stories: 'Patient Stories',
  appointments: 'Appointment Request',
};

// Accepts an optional `slug` prop so the same component can edit any org_sites
// tenant (ABMHC, Bishop Manjoro, …). Falls back to 'abmhc' for legacy callers
// that haven't passed a slug yet.
const ABMHCSiteManager = ({ slug = SITE_SLUG_FALLBACK }) => {
  const SITE_SLUG = slug;
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState(null);

  const { data: site, isLoading } = useQuery({
    queryKey: ['org-site-admin', SITE_SLUG],
    queryFn: () => api.get(`/sites/admin/${SITE_SLUG}`).then((r) => r.data),
  });

  useEffect(() => {
    if (site && !draft) setDraft(JSON.parse(JSON.stringify(site)));
  }, [site, draft]);

  const saveMutation = useMutation({
    mutationFn: (payload) => api.put(`/sites/admin/${SITE_SLUG}`, payload),
    onSuccess: () => {
      toast.success('ABMHC site updated');
      queryClient.invalidateQueries({ queryKey: ['org-site-admin', SITE_SLUG] });
    },
    onError: (e) => toast.error(e.response?.data?.detail || e.message || 'Save failed'),
  });

  if (isLoading || !draft) {
    return (
      <div className="p-12 text-center text-slate-500">
        <Loader2 className="h-6 w-6 animate-spin inline mr-2" /> Loading ABMHC site…
      </div>
    );
  }

  const updateBlock = (idx, patch) => {
    const next = [...draft.blocks];
    next[idx] = { ...next[idx], ...patch };
    setDraft({ ...draft, blocks: next });
  };
  const updateBlockProps = (idx, propPatch) => {
    const next = [...draft.blocks];
    next[idx] = { ...next[idx], props: { ...(next[idx].props || {}), ...propPatch } };
    setDraft({ ...draft, blocks: next });
  };
  const moveBlock = (idx, dir) => {
    const next = [...draft.blocks];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setDraft({ ...draft, blocks: next });
  };

  const save = () => saveMutation.mutate({
    name: draft.name,
    tagline: draft.tagline,
    logo_letter: draft.logo_letter,
    theme: draft.theme,
    seo: draft.seo,
    nav_links: draft.nav_links,
    blocks: draft.blocks,
    is_published: draft.is_published,
    domain_aliases: draft.domain_aliases,
  });

  // ---------- block library (add new blocks) ----------
  const BLOCK_TEMPLATES = {
    hero: { type: 'hero', enabled: true, props: { kicker: 'NEW SECTION', title: 'Section Title', tagline: '', cta_label: 'Learn More', cta_href: '#', image_url: '' } },
    about: { type: 'about', enabled: true, props: { heading: 'About Us', body: '', image_url: '' } },
    services: { type: 'services', enabled: true, props: { heading: 'Our Services', items: [] } },
    doctors: { type: 'doctors', enabled: true, props: { heading: 'Our Team', items: [] } },
    patient_info: { type: 'patient_info', enabled: true, props: { heading: 'Patient Information', items: [] } },
    news: { type: 'news', enabled: true, props: { heading: 'News', items: [] } },
    gallery: { type: 'gallery', enabled: true, props: { heading: 'Gallery', items: [] } },
    donate: { type: 'donate', enabled: true, props: { heading: 'Donate', body: '', cta_label: 'Donate Now', cta_href: '/campaigns/abmhc' } },
    contact: { type: 'contact', enabled: true, props: { heading: 'Contact', email: '', phone: '', address: '', map_embed_url: '' } },
    footer: { type: 'footer', enabled: true, props: { copyright: '', social_links: [] } },
    // Church-specific
    leadership: { type: 'leadership', enabled: true, props: { heading: 'Leadership', items: [] } },
    service_times: { type: 'service_times', enabled: true, props: { heading: 'Service Times', items: [] } },
    ministries: { type: 'ministries', enabled: true, props: { heading: 'Ministries', items: [] } },
    sermons: { type: 'sermons', enabled: true, props: { heading: 'Sermons & Media', items: [] } },
    tribute: { type: 'tribute', enabled: true, props: { heading: 'Tribute Wall', subhead: '', items: [] } },
    events: { type: 'events', enabled: true, props: { heading: 'Upcoming Events', items: [] } },
    livestream: { type: 'livestream', enabled: true, props: { heading: 'Watch Live', subhead: '', url: '', is_live: false } },
    prayer_requests: { type: 'prayer_requests', enabled: true, props: { heading: 'Prayer Requests', subhead: 'Share your request with our intercessory team.', show_public_wall: false } },
    milestones: { type: 'milestones', enabled: true, props: { kicker: 'Construction Progress', heading: 'Project Milestones', subhead: 'Track our journey from groundbreaking to completion', items: [], cta_label: 'View All Milestones', cta_href: '#milestones' } },
    quick_links: { type: 'quick_links', enabled: true, props: { items: [] } },
    funding_progress: { type: 'funding_progress', enabled: true, props: { kicker: 'Funding Progress', heading: 'Help Us Reach Our Goal', subhead: 'Track our fundraising progress', currency: 'USD', goal: 100000, override_raised: 0, cta_label: 'Make a Donation', cta_href: '#donate' } },
    newsletter: { type: 'newsletter', enabled: true, props: { kicker: 'Stay Connected', heading: 'Join Our Community', body: '', bullets: [], form_heading: 'Stay Updated', form_sub: 'Get updates delivered to your inbox.', categories: [] } },
    patient_stories: { type: 'patient_stories', enabled: true, props: { kicker: 'Patient Stories', heading: 'Healing in the Community', subhead: '', items: [], allow_submissions: true } },
    appointments: { type: 'appointments', enabled: true, props: { kicker: 'Book an Appointment', heading: 'Request an Appointment', body: '', contact_phone: '', departments: ['General Consultation', 'Maternal Health', 'Emergency', 'Pharmacy'] } },
  };

  const addBlock = (type) => {
    const tmpl = BLOCK_TEMPLATES[type];
    if (!tmpl) return;
    const newBlock = { ...tmpl, id: `${type}-${Date.now()}` };
    setDraft({ ...draft, blocks: [...draft.blocks, newBlock] });
  };
  const removeBlock = (idx) => {
    if (!window.confirm('Remove this block? This cannot be undone until you reload.')) return;
    setDraft({ ...draft, blocks: draft.blocks.filter((_, i) => i !== idx) });
  };

  return (
    <div className="space-y-4" data-testid="abmhc-site-manager">
      {/* prominent publish banner — visitors see 404 when off */}
      {!draft.is_published && (
        <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-3 flex items-center justify-between gap-3" data-testid="draft-banner">
          <div className="flex items-center gap-2 text-amber-900 text-sm">
            <span className="font-bold uppercase tracking-wider text-xs bg-amber-200 px-2 py-0.5 rounded">Draft</span>
            <span>This site is <strong>not live</strong>. Visitors to <code>/{SITE_SLUG}</code> see a 404 page.</span>
          </div>
          <Button
            size="sm"
            onClick={() => {
              setDraft({ ...draft, is_published: true });
              saveMutation.mutate({ ...draft, is_published: true });
            }}
            data-testid="header-publish-now"
          >
            Publish Now
          </Button>
        </div>
      )}

      {/* header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Globe className="h-5 w-5 text-teal-600" />
            {draft.name || SITE_SLUG.toUpperCase()} Website CMS
            <Badge variant={draft.is_published ? 'default' : 'secondary'} data-testid="header-publish-badge">
              {draft.is_published ? 'Published' : 'Draft'}
            </Badge>
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Edit the public site at <code>/{SITE_SLUG}</code> (and <code>{SITE_SLUG}.&lt;your-domain&gt;</code> once DNS is mapped).
          </p>
        </div>
        <div className="flex gap-2">
          <a href={`/${SITE_SLUG}`} target="_blank" rel="noreferrer">
            <Button size="sm" variant="outline" data-testid="abmhc-preview-btn">
              <ExternalLink className="h-4 w-4 mr-1" /> View Site
            </Button>
          </a>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-md border bg-white">
            <span className="text-xs text-slate-500">Live</span>
            <Switch
              checked={!!draft.is_published}
              onCheckedChange={(v) => {
                setDraft({ ...draft, is_published: v });
                saveMutation.mutate({ ...draft, is_published: v });
              }}
              data-testid="header-publish-switch"
            />
          </div>
          <Button size="sm" onClick={save} disabled={saveMutation.isPending} data-testid="abmhc-save-btn">
            {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
            Save Changes
          </Button>
        </div>
      </div>

      <Tabs defaultValue="blocks" className="w-full">
        <TabsList className="grid w-full grid-cols-2 sm:grid-cols-4">
          <TabsTrigger value="blocks" data-testid="abmhc-tab-blocks">Blocks</TabsTrigger>
          <TabsTrigger value="branding" data-testid="abmhc-tab-branding">Branding & Nav</TabsTrigger>
          <TabsTrigger value="info" data-testid="abmhc-tab-info">SEO & Publish</TabsTrigger>
          <TabsTrigger value="domains" data-testid="abmhc-tab-domains">Domains</TabsTrigger>
        </TabsList>

        {/* ----- BLOCKS ----- */}
        <TabsContent value="blocks" className="space-y-3">
          {draft.blocks.map((block, idx) => (
            <Card key={block.id} className="overflow-hidden">
              <CardHeader className="py-3 flex flex-row items-center justify-between gap-2 bg-slate-50">
                <div className="flex items-center gap-2">
                  <CardTitle className="text-sm">
                    {BLOCK_LABELS[block.type] || block.type}
                  </CardTitle>
                  <Badge variant="outline" className="text-xs">{block.type}</Badge>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="icon" variant="ghost" onClick={() => moveBlock(idx, -1)} disabled={idx === 0} title="Move up">↑</Button>
                  <Button size="icon" variant="ghost" onClick={() => moveBlock(idx, 1)} disabled={idx === draft.blocks.length - 1} title="Move down">↓</Button>
                  <Switch
                    checked={!!block.enabled}
                    onCheckedChange={(v) => updateBlock(idx, { enabled: v })}
                    data-testid={`abmhc-block-toggle-${block.type}`}
                  />
                  <Button size="icon" variant="ghost" onClick={() => removeBlock(idx)} title="Delete block" data-testid={`abmhc-block-remove-${block.type}`}>
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="py-3 space-y-2">
                <BlockEditor block={block} onChange={(propPatch) => updateBlockProps(idx, propPatch)} />
              </CardContent>
            </Card>
          ))}

          {/* Add block menu */}
          <Card className="bg-slate-50 border-dashed">
            <CardContent className="py-4">
              <p className="text-xs text-slate-500 mb-2">Add a new block</p>
              <div className="flex flex-wrap gap-2">
                {Object.keys(BLOCK_TEMPLATES).map((t) => (
                  <Button
                    key={t}
                    size="sm"
                    variant="outline"
                    onClick={() => addBlock(t)}
                    data-testid={`abmhc-add-block-${t}`}
                  >
                    <Plus className="h-3 w-3 mr-1" /> {BLOCK_LABELS[t] || t}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ----- BRANDING & NAV ----- */}
        <TabsContent value="branding" className="space-y-3">
          <Card>
            <CardHeader><CardTitle className="text-sm">Logo & Header</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Site name</Label>
                  <Input value={draft.name || ''} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="abmhc-site-name" />
                </div>
                <div>
                  <Label>Logo monogram (1 letter)</Label>
                  <Input maxLength={2} value={draft.logo_letter || ''} onChange={(e) => setDraft({ ...draft, logo_letter: e.target.value })} data-testid="abmhc-logo-letter" />
                </div>
              </div>
              <div>
                <Label>Tagline (under the site name in the header)</Label>
                <Input value={draft.tagline || ''} onChange={(e) => setDraft({ ...draft, tagline: e.target.value })} data-testid="abmhc-tagline" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Primary color (navy)</Label>
                  <Input type="color" value={draft.theme?.primary || '#1c3e6e'} onChange={(e) => setDraft({ ...draft, theme: { ...(draft.theme || {}), primary: e.target.value } })} className="h-10" />
                </div>
                <div>
                  <Label>Accent color (CTA)</Label>
                  <Input type="color" value={draft.theme?.accent || '#f59052'} onChange={(e) => setDraft({ ...draft, theme: { ...(draft.theme || {}), accent: e.target.value } })} className="h-10" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-sm">Navigation Links</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              <p className="text-xs text-slate-500">Top menu items. Use anchors like <code>#about</code> for in-page jumps or absolute paths like <code>/campaigns/abmhc</code> for external pages.</p>
              {(draft.nav_links || []).map((nl, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <Input
                    placeholder="Label"
                    value={nl.label || ''}
                    onChange={(e) => {
                      const next = [...(draft.nav_links || [])];
                      next[i] = { ...next[i], label: e.target.value };
                      setDraft({ ...draft, nav_links: next });
                    }}
                    className="flex-1"
                    data-testid={`abmhc-nav-label-${i}`}
                  />
                  <Input
                    placeholder="#hero or /campaigns/abmhc"
                    value={nl.href || ''}
                    onChange={(e) => {
                      const next = [...(draft.nav_links || [])];
                      next[i] = { ...next[i], href: e.target.value };
                      setDraft({ ...draft, nav_links: next });
                    }}
                    className="flex-1"
                    data-testid={`abmhc-nav-href-${i}`}
                  />
                  <Button size="icon" variant="ghost" onClick={() => setDraft({ ...draft, nav_links: (draft.nav_links || []).filter((_, x) => x !== i) })}>
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              ))}
              <Button size="sm" variant="outline" onClick={() => setDraft({ ...draft, nav_links: [...(draft.nav_links || []), { label: '', href: '' }] })} data-testid="abmhc-nav-add">
                <Plus className="h-3 w-3 mr-1" /> Add link
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ----- SEO & PUBLISH ----- */}
        <TabsContent value="info" className="space-y-3">
          <Card><CardContent className="space-y-3 pt-4">
            <div>
              <Label>SEO title (shown in browser tab + search results)</Label>
              <Input value={draft.seo?.title || ''} onChange={(e) => setDraft({ ...draft, seo: { ...(draft.seo || {}), title: e.target.value } })} data-testid="abmhc-seo-title" />
            </div>
            <div>
              <Label>SEO description</Label>
              <Textarea rows={2} value={draft.seo?.description || ''} onChange={(e) => setDraft({ ...draft, seo: { ...(draft.seo || {}), description: e.target.value } })} data-testid="abmhc-seo-desc" />
            </div>
            <div>
              <MediaField label="Favicon" value={draft.seo?.favicon_url || ''} onChange={(url) => setDraft({ ...draft, seo: { ...(draft.seo || {}), favicon_url: url } })} accept="image/*" testId="abmhc-favicon-upload" />
            </div>
            <div>
              <MediaField label="Open Graph image (for link previews on social)" value={draft.seo?.og_image || ''} onChange={(url) => setDraft({ ...draft, seo: { ...(draft.seo || {}), og_image: url } })} accept="image/*" testId="abmhc-og-upload" />
            </div>
            <div className="flex items-center justify-between pt-2 border-t">
              <div>
                <Label>Publish site</Label>
                <p className="text-xs text-slate-500">When off, /abmhc returns 404 to the public.</p>
              </div>
              <Switch checked={!!draft.is_published} onCheckedChange={(v) => setDraft({ ...draft, is_published: v })} data-testid="abmhc-publish-switch" />
            </div>
          </CardContent></Card>
        </TabsContent>

        {/* ----- DOMAINS ----- */}
        <TabsContent value="domains">
          <Card><CardContent className="space-y-3 pt-4">
            <p className="text-xs text-slate-500">
              Add custom domains (e.g. <code>abmhc.org</code>) that should serve this site.
              Subdomain <code>abmhc.&lt;your-platform-domain&gt;</code> works automatically — no entry needed here.
            </p>
            {(draft.domain_aliases || []).map((d, i) => (
              <div key={i} className="flex gap-2">
                <Input
                  value={d}
                  onChange={(e) => {
                    const next = [...(draft.domain_aliases || [])];
                    next[i] = e.target.value.toLowerCase().trim();
                    setDraft({ ...draft, domain_aliases: next });
                  }}
                  placeholder="abmhc.org"
                  data-testid={`abmhc-domain-${i}`}
                />
                <Button size="icon" variant="ghost" onClick={() => setDraft({ ...draft, domain_aliases: (draft.domain_aliases || []).filter((_, x) => x !== i) })}>
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>
            ))}
            <Button size="sm" variant="outline" onClick={() => setDraft({ ...draft, domain_aliases: [...(draft.domain_aliases || []), ''] })}>
              <Plus className="h-3 w-3 mr-1" /> Add domain
            </Button>
          </CardContent></Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

// ---------- per-block structured editors ----------
const Field = ({ label, value, onChange, type = 'text', textarea = false, rows = 2, ...rest }) => (
  <div className="space-y-1">
    <Label className="text-xs uppercase tracking-wide text-slate-500">{label}</Label>
    {textarea ? (
      <Textarea rows={rows} value={value || ''} onChange={(e) => onChange(e.target.value)} {...rest} />
    ) : (
      <Input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)} {...rest} />
    )}
  </div>
);

const BlockEditor = ({ block, onChange }) => {
  const p = block.props || {};
  switch (block.type) {
    case 'hero':
      return (
        <>
          <Field label="Kicker" value={p.kicker} onChange={(v) => onChange({ kicker: v })} />
          <Field label="Title" value={p.title} onChange={(v) => onChange({ title: v })} />
          <Field label="Tagline" value={p.tagline} onChange={(v) => onChange({ tagline: v })} textarea rows={2} />
          <div className="grid grid-cols-2 gap-2">
            <Field label="Primary CTA label" value={p.cta_label} onChange={(v) => onChange({ cta_label: v })} />
            <Field label="Primary CTA link" value={p.cta_href} onChange={(v) => onChange({ cta_href: v })} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Secondary CTA label (optional)" value={p.secondary_cta_label} onChange={(v) => onChange({ secondary_cta_label: v })} />
            <Field label="Secondary CTA link (optional)" value={p.secondary_cta_href} onChange={(v) => onChange({ secondary_cta_href: v })} />
          </div>
          <MediaField label="Background image / video" value={p.image_url} onChange={(v) => onChange({ image_url: v })} testId="hero-media" />
        </>
      );
    case 'about':
      return (
        <>
          <Field label="Heading" value={p.heading} onChange={(v) => onChange({ heading: v })} />
          <Field label="Body" value={p.body} onChange={(v) => onChange({ body: v })} textarea rows={4} />
          <MediaField label="Image" value={p.image_url} onChange={(v) => onChange({ image_url: v })} accept="image/*" testId="about-media" />
        </>
      );
    case 'services':
      return (
        <>
          <Field label="Heading" value={p.heading} onChange={(v) => onChange({ heading: v })} />
          <ItemListEditor
            items={p.items || []}
            onChange={(items) => onChange({ items })}
            fields={[{ key: 'name', label: 'Name' }, { key: 'description', label: 'Description', textarea: true }]}
          />
        </>
      );
    case 'doctors':
      return (
        <>
          <Field label="Heading" value={p.heading} onChange={(v) => onChange({ heading: v })} />
          <ItemListEditor
            items={p.items || []}
            onChange={(items) => onChange({ items })}
            fields={[
              { key: 'name', label: 'Name' },
              { key: 'title', label: 'Title / Specialty' },
              { key: 'photo_url', label: 'Photo', media: true, accept: 'image/*' },
              { key: 'bio', label: 'Bio', textarea: true },
            ]}
          />
        </>
      );
    case 'patient_info':
      return (
        <>
          <Field label="Heading" value={p.heading} onChange={(v) => onChange({ heading: v })} />
          <ItemListEditor
            items={p.items || []}
            onChange={(items) => onChange({ items })}
            fields={[{ key: 'label', label: 'Label' }, { key: 'value', label: 'Value' }]}
          />
        </>
      );
    case 'news':
      return (
        <>
          <Field label="Heading" value={p.heading} onChange={(v) => onChange({ heading: v })} />
          <ItemListEditor
            items={p.items || []}
            onChange={(items) => onChange({ items })}
            fields={[
              { key: 'date', label: 'Date' },
              { key: 'title', label: 'Title' },
              { key: 'body', label: 'Body', textarea: true },
            ]}
          />
        </>
      );
    case 'gallery':
      return (
        <>
          <Field label="Heading" value={p.heading} onChange={(v) => onChange({ heading: v })} />
          <ItemListEditor
            items={p.items || []}
            onChange={(items) => onChange({ items })}
            fields={[
              { key: 'url', label: 'Image / video', media: true, accept: 'image/*,video/*' },
              { key: 'caption', label: 'Caption' },
            ]}
          />
        </>
      );
    case 'donate':
      return (
        <>
          <Field label="Heading" value={p.heading} onChange={(v) => onChange({ heading: v })} />
          <Field label="Body" value={p.body} onChange={(v) => onChange({ body: v })} textarea />
          <div className="grid grid-cols-2 gap-2">
            <Field label="CTA label (used when Stripe is OFF)" value={p.cta_label} onChange={(v) => onChange({ cta_label: v })} />
            <Field label="CTA link (used when Stripe is OFF)" value={p.cta_href} onChange={(v) => onChange({ cta_href: v })} />
          </div>
          <div className="bg-slate-50 border rounded p-3 space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-wide text-slate-500">Enable Stripe card checkout</Label>
              <Switch
                checked={!!p.stripe_enabled}
                onCheckedChange={(v) => onChange({ stripe_enabled: v })}
                data-testid="donate-stripe-switch"
              />
            </div>
            <p className="text-xs text-slate-500">When ON, visitors see a card-payment form right on this block. Stripe must be configured in the server environment.</p>
            <div className="grid grid-cols-2 gap-2">
              <Field label="Currency (USD, EUR, ZAR, …)" value={p.currency || 'USD'} onChange={(v) => onChange({ currency: v.toUpperCase().slice(0, 3) })} />
              <Field
                label="Suggested amounts (comma separated)"
                value={(p.suggested_amounts || [10, 25, 50, 100, 250]).join(', ')}
                onChange={(v) => {
                  const arr = v.split(',').map((s) => Number(s.trim())).filter((n) => n > 0);
                  onChange({ suggested_amounts: arr });
                }}
              />
            </div>
          </div>
          <div className="bg-slate-50 border rounded p-3 space-y-2">
            <Label className="text-xs uppercase tracking-wide text-slate-500">Offline / bank transfer (optional)</Label>
            <Field label="Heading" value={p.bank_heading} onChange={(v) => onChange({ bank_heading: v })} />
            <Field
              label="Bank details (free-form — account name, number, branch, swift, etc.)"
              value={p.bank_details}
              onChange={(v) => onChange({ bank_details: v })}
              textarea
              rows={4}
            />
            <Field label="WhatsApp number for proof-of-payment (e.g. +263771234567)" value={p.whatsapp_confirm} onChange={(v) => onChange({ whatsapp_confirm: v })} />
          </div>
        </>
      );
    case 'contact':
      return (
        <>
          <Field label="Heading" value={p.heading} onChange={(v) => onChange({ heading: v })} />
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <Field label="Email" value={p.email} onChange={(v) => onChange({ email: v })} />
            <Field label="Phone" value={p.phone} onChange={(v) => onChange({ phone: v })} />
            <Field label="Address" value={p.address} onChange={(v) => onChange({ address: v })} />
          </div>
          <Field label="Map embed URL (Google Maps iframe src)" value={p.map_embed_url} onChange={(v) => onChange({ map_embed_url: v })} />
        </>
      );
    case 'footer':
      return (
        <>
          <Field label="Copyright" value={p.copyright} onChange={(v) => onChange({ copyright: v })} />
          <ItemListEditor
            items={p.social_links || []}
            onChange={(social_links) => onChange({ social_links })}
            fields={[{ key: 'label', label: 'Label' }, { key: 'url', label: 'URL' }]}
          />
        </>
      );
    // ----- Church-specific -----
    case 'leadership':
      return (
        <>
          <Field label="Heading" value={p.heading} onChange={(v) => onChange({ heading: v })} />
          <ItemListEditor
            items={p.items || []}
            onChange={(items) => onChange({ items })}
            fields={[
              { key: 'name', label: 'Name' },
              { key: 'title', label: 'Title / Role' },
              { key: 'photo_url', label: 'Photo', media: true, accept: 'image/*' },
              { key: 'bio', label: 'Bio', textarea: true },
            ]}
          />
        </>
      );
    case 'service_times':
      return (
        <>
          <Field label="Heading" value={p.heading} onChange={(v) => onChange({ heading: v })} />
          <ItemListEditor
            items={p.items || []}
            onChange={(items) => onChange({ items })}
            fields={[
              { key: 'day', label: 'Day' },
              { key: 'time', label: 'Time' },
              { key: 'service', label: 'Service name' },
            ]}
          />
        </>
      );
    case 'ministries':
      return (
        <>
          <Field label="Heading" value={p.heading} onChange={(v) => onChange({ heading: v })} />
          <ItemListEditor
            items={p.items || []}
            onChange={(items) => onChange({ items })}
            fields={[
              { key: 'name', label: 'Name' },
              { key: 'description', label: 'Description', textarea: true },
            ]}
          />
        </>
      );
    case 'sermons':
      return (
        <>
          <Field label="Heading" value={p.heading} onChange={(v) => onChange({ heading: v })} />
          <ItemListEditor
            items={p.items || []}
            onChange={(items) => onChange({ items })}
            fields={[
              { key: 'title', label: 'Title' },
              { key: 'date', label: 'Date' },
              { key: 'audio_url', label: 'Audio', media: true, accept: 'audio/*' },
              { key: 'video_url', label: 'Video', media: true, accept: 'video/*' },
              { key: 'thumbnail', label: 'Thumbnail', media: true, accept: 'image/*' },
            ]}
          />
        </>
      );
    case 'tribute':
      return (
        <>
          <Field label="Heading" value={p.heading} onChange={(v) => onChange({ heading: v })} />
          <Field label="Subhead" value={p.subhead} onChange={(v) => onChange({ subhead: v })} />
          <ItemListEditor
            items={p.items || []}
            onChange={(items) => onChange({ items })}
            fields={[
              { key: 'name', label: 'Author' },
              { key: 'date', label: 'Date' },
              { key: 'message', label: 'Message', textarea: true },
            ]}
          />
        </>
      );
    case 'events':
      return (
        <>
          <Field label="Heading" value={p.heading} onChange={(v) => onChange({ heading: v })} />
          <ItemListEditor
            items={p.items || []}
            onChange={(items) => onChange({ items })}
            fields={[
              { key: 'date', label: 'Date (e.g. 24 Mar 2026 · 18:00)' },
              { key: 'title', label: 'Title' },
              { key: 'location', label: 'Location' },
              { key: 'description', label: 'Description', textarea: true },
              { key: 'cta_label', label: 'CTA label' },
              { key: 'cta_href', label: 'CTA link' },
            ]}
          />
        </>
      );
    case 'livestream':
      return (
        <>
          <Field label="Heading" value={p.heading} onChange={(v) => onChange({ heading: v })} />
          <Field label="Subhead" value={p.subhead} onChange={(v) => onChange({ subhead: v })} />
          <Field
            label="Live URL (YouTube live, youtu.be link, or Facebook video URL)"
            value={p.url}
            onChange={(v) => onChange({ url: v })}
          />
          <div className="flex items-center justify-between bg-slate-50 border rounded p-3">
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">Currently live</Label>
              <p className="text-xs text-slate-500">When ON, a pulsing red &ldquo;LIVE&rdquo; tag appears next to the heading.</p>
            </div>
            <Switch
              checked={!!p.is_live}
              onCheckedChange={(v) => onChange({ is_live: v })}
              data-testid="livestream-live-switch"
            />
          </div>
        </>
      );
    case 'prayer_requests':
      return (
        <>
          <Field label="Heading" value={p.heading} onChange={(v) => onChange({ heading: v })} />
          <Field label="Subhead" value={p.subhead} onChange={(v) => onChange({ subhead: v })} textarea rows={2} />
          <div className="flex items-center justify-between bg-slate-50 border rounded p-3">
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">Show public prayer wall</Label>
              <p className="text-xs text-slate-500">If OFF, requests stay private. Even when ON, an admin must still approve each request before it appears.</p>
            </div>
            <Switch
              checked={!!p.show_public_wall}
              onCheckedChange={(v) => onChange({ show_public_wall: v })}
              data-testid="prayer-wall-switch"
            />
          </div>
          <p className="text-xs text-slate-500 pt-2">Moderate submissions at <code>/admin/prayer-requests</code>.</p>
        </>
      );
    case 'milestones':
      return (
        <>
          <Field label="Kicker" value={p.kicker} onChange={(v) => onChange({ kicker: v })} />
          <Field label="Heading" value={p.heading} onChange={(v) => onChange({ heading: v })} />
          <Field label="Subhead" value={p.subhead} onChange={(v) => onChange({ subhead: v })} textarea rows={2} />
          <ItemListEditor
            items={p.items || []}
            onChange={(items) => onChange({ items })}
            fields={[
              { key: 'title', label: 'Phase title' },
              { key: 'date', label: 'Date / target' },
              { key: 'status', label: 'Status (completed | in_progress | upcoming)' },
              { key: 'description', label: 'Description', textarea: true },
            ]}
          />
          <div className="grid grid-cols-2 gap-2">
            <Field label="CTA label" value={p.cta_label} onChange={(v) => onChange({ cta_label: v })} />
            <Field label="CTA link" value={p.cta_href} onChange={(v) => onChange({ cta_href: v })} />
          </div>
        </>
      );
    case 'quick_links':
      return (
        <ItemListEditor
          items={p.items || []}
          onChange={(items) => onChange({ items })}
          fields={[
            { key: 'title', label: 'Card title' },
            { key: 'description', label: 'Description', textarea: true },
            { key: 'icon', label: 'Icon (milestones | gallery | reports | contact | stories | donate)' },
            { key: 'href', label: 'Link' },
            { key: 'cta_label', label: 'CTA label (e.g. "Learn more")' },
          ]}
        />
      );
    case 'funding_progress':
      return (
        <>
          <Field label="Kicker" value={p.kicker} onChange={(v) => onChange({ kicker: v })} />
          <Field label="Heading" value={p.heading} onChange={(v) => onChange({ heading: v })} />
          <Field label="Subhead" value={p.subhead} onChange={(v) => onChange({ subhead: v })} textarea rows={2} />
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            <Field label="Currency" value={p.currency || 'USD'} onChange={(v) => onChange({ currency: v.toUpperCase().slice(0, 3) })} />
            <Field label="Goal amount" type="number" value={p.goal} onChange={(v) => onChange({ goal: Number(v) })} />
            <Field label="Override raised (optional, for offline gifts)" type="number" value={p.override_raised} onChange={(v) => onChange({ override_raised: Number(v) })} />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Field label="CTA label" value={p.cta_label} onChange={(v) => onChange({ cta_label: v })} />
            <Field label="CTA link" value={p.cta_href} onChange={(v) => onChange({ cta_href: v })} />
          </div>
          <p className="text-xs text-slate-500">Real-time raised amount is pulled from Stripe site donations automatically.</p>
        </>
      );
    case 'newsletter':
      return (
        <>
          <Field label="Kicker" value={p.kicker} onChange={(v) => onChange({ kicker: v })} />
          <Field label="Heading" value={p.heading} onChange={(v) => onChange({ heading: v })} />
          <Field label="Body" value={p.body} onChange={(v) => onChange({ body: v })} textarea rows={3} />
          <Field
            label="Bullet points (one per line)"
            value={(p.bullets || []).join('\n')}
            onChange={(v) => onChange({ bullets: v.split('\n').map((s) => s.trim()).filter(Boolean) })}
            textarea
            rows={3}
          />
          <div className="grid grid-cols-2 gap-2">
            <Field label="Form heading" value={p.form_heading} onChange={(v) => onChange({ form_heading: v })} />
            <Field label="Form sub-text" value={p.form_sub} onChange={(v) => onChange({ form_sub: v })} />
          </div>
          <Field
            label="Category checkboxes (one per line, optional)"
            value={(p.categories || []).join('\n')}
            onChange={(v) => onChange({ categories: v.split('\n').map((s) => s.trim()).filter(Boolean) })}
            textarea
            rows={2}
          />
          <p className="text-xs text-slate-500">View subscribers at <code>/admin/newsletter-subscribers</code> (coming soon).</p>
        </>
      );
    case 'patient_stories':
      return (
        <>
          <Field label="Kicker" value={p.kicker} onChange={(v) => onChange({ kicker: v })} />
          <Field label="Heading" value={p.heading} onChange={(v) => onChange({ heading: v })} />
          <Field label="Subhead" value={p.subhead} onChange={(v) => onChange({ subhead: v })} textarea rows={2} />
          <div className="flex items-center justify-between bg-slate-50 border rounded p-3">
            <Label className="text-xs uppercase tracking-wide text-slate-500">Allow public submissions</Label>
            <Switch
              checked={p.allow_submissions !== false}
              onCheckedChange={(v) => onChange({ allow_submissions: v })}
            />
          </div>
          <ItemListEditor
            items={p.items || []}
            onChange={(items) => onChange({ items })}
            fields={[
              { key: 'name', label: 'Name' },
              { key: 'title', label: 'Caption (e.g. Mother of two)' },
              { key: 'photo_url', label: 'Photo', media: true, accept: 'image/*' },
              { key: 'message', label: 'Story', textarea: true },
            ]}
          />
          <p className="text-xs text-slate-500">Moderate pending submissions at <code>/admin/patient-stories</code>.</p>
        </>
      );
    case 'appointments':
      return (
        <>
          <Field label="Kicker" value={p.kicker} onChange={(v) => onChange({ kicker: v })} />
          <Field label="Heading" value={p.heading} onChange={(v) => onChange({ heading: v })} />
          <Field label="Body" value={p.body} onChange={(v) => onChange({ body: v })} textarea rows={3} />
          <Field label="Contact phone (shown next to form)" value={p.contact_phone} onChange={(v) => onChange({ contact_phone: v })} />
          <Field
            label="Departments (one per line)"
            value={(p.departments || []).join('\n')}
            onChange={(v) => onChange({ departments: v.split('\n').map((s) => s.trim()).filter(Boolean) })}
            textarea
            rows={4}
          />
          <p className="text-xs text-slate-500">View requests at <code>/admin/appointments</code>.</p>
        </>
      );
    default:
      return (
        <Field
          label="Raw props (JSON)"
          value={JSON.stringify(p, null, 2)}
          onChange={(v) => {
            try { onChange(JSON.parse(v)); } catch { /* ignore parse error mid-typing */ }
          }}
          textarea
          rows={4}
        />
      );
  }
};

const ItemListEditor = ({ items, onChange, fields }) => {
  const update = (idx, patch) => {
    const next = [...items];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };
  const add = () => onChange([...items, fields.reduce((acc, f) => ({ ...acc, [f.key]: '' }), {})]);
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));
  return (
    <div className="space-y-2">
      {items.map((it, idx) => (
        <div key={idx} className="border rounded p-2 space-y-2 bg-white">
          <div className="grid grid-cols-2 gap-2">
            {fields.map((f) => (
              f.media ? (
                <MediaField
                  key={f.key}
                  label={f.label}
                  value={it[f.key]}
                  onChange={(v) => update(idx, { [f.key]: v })}
                  accept={f.accept || 'image/*'}
                />
              ) : (
                <Field
                  key={f.key}
                  label={f.label}
                  value={it[f.key]}
                  onChange={(v) => update(idx, { [f.key]: v })}
                  textarea={f.textarea}
                />
              )
            ))}
          </div>
          <Button size="sm" variant="ghost" onClick={() => remove(idx)}>
            <Trash2 className="h-3 w-3 mr-1 text-red-500" /> Remove
          </Button>
        </div>
      ))}
      <Button size="sm" variant="outline" onClick={add}>
        <Plus className="h-3 w-3 mr-1" /> Add item
      </Button>
    </div>
  );
};

export default ABMHCSiteManager;
